import React from "react";
import { Table } from "react-bootstrap";
import { ActionMenu } from "../../../../components/Ads/ActionMenu/ActionMenu";
import type { Advertiser } from "../../../../api/ads";
import { AdsAdvertiserStatus } from "../../../../api/ads";

import "./AdvertiserTable.css";

export interface AdvertiserTableProps {
  advertisers: Advertiser[];
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  onBlock: (id: string) => void;
  onViewCampaigns: (advertiser: Advertiser) => void;
  onViewBilling: (advertiser: Advertiser) => void;
}

type TagKey = "approved" | "pending" | "blocked" | "rejected";

const statusTagKey = (status: AdsAdvertiserStatus): TagKey => {
  switch (status) {
    case AdsAdvertiserStatus.APPROVED:
      return "approved";
    case AdsAdvertiserStatus.REJECTED:
      return "rejected";
    case AdsAdvertiserStatus.DISABLED:
      return "blocked";
    case AdsAdvertiserStatus.PENDING:
    default:
      return "pending";
  }
};

const statusLabel = (status: AdsAdvertiserStatus): string => {
  const s = status.toString();
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
};

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "2-digit" });
};

export const AdvertiserTable: React.FC<AdvertiserTableProps> = ({
  advertisers,
  onApprove,
  onReject,
  onBlock,
  onViewCampaigns,
  onViewBilling,
}) => {
  return (
    <div className="ads-table-wrapper">
      <Table responsive className="ads-table">
        <thead>
          <tr>
            <th>Advertiser Name</th>
            <th>Contact Email</th>
            <th>Status</th>
            <th>Created At</th>
            <th className="text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          {advertisers.map((a) => (
            <tr key={a.id}>
              <td>{a.name}</td>
              <td>{a.contactEmail}</td>
              <td>
                <span className={`ads-tag ads-tag--${statusTagKey(a.status)}`}>
                  {statusLabel(a.status)}
                </span>
              </td>
              <td>{formatDate(a.createdAt)}</td>
              <td className="text-center">
                <ActionMenu
                  items={[
                    {
                      key: "approve",
                      label: "Approve",
                      iconClassName: "ph-check",
                      anyOfPermissions: ["ads.admin", "ads.manage"],
                      hideWhenDisabled: true,
                      disabled: a.status === AdsAdvertiserStatus.APPROVED,
                      onSelect: () => onApprove(a.id),
                    },
                    {
                      key: "reject",
                      label: "Reject",
                      iconClassName: "ph-x",
                      anyOfPermissions: ["ads.admin", "ads.manage"],
                      hideWhenDisabled: true,
                      disabled: a.status === AdsAdvertiserStatus.REJECTED,
                      onSelect: () => onReject(a.id),
                      isDanger: true,
                    },
                    {
                      key: "view-campaigns",
                      label: "View Campaigns",
                      iconClassName: "ph-list",
                      anyOfPermissions: ["ads.view", "ads.admin", "ads.manage"],
                      hideWhenDisabled: true,
                      onSelect: () => onViewCampaigns(a),
                    },
                    {
                      key: "billing",
                      label: "Billing History",
                      iconClassName: "ph-credit-card",
                      anyOfPermissions: ["ads.view", "ads.admin", "ads.manage"],
                      hideWhenDisabled: true,
                      onSelect: () => onViewBilling(a),
                    },
                    {
                      key: "block",
                      label: "Block",
                      iconClassName: "ph-ban",
                      anyOfPermissions: ["ads.admin", "ads.manage"],
                      hideWhenDisabled: true,
                      disabled: a.status === AdsAdvertiserStatus.DISABLED,
                      onSelect: () => onBlock(a.id),
                      isDanger: true,
                    },
                  ]}
                  ariaLabel={`Advertiser actions for ${a.name}`}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};
